/**
 * OutcomeFormDialog — J1-B / Wave A F4+F5 (BUG-3 rework)
 *
 * Create / Edit dialog for Outcomes.
 * Uses a custom FormSheet to allow:
 *   - Conditional parent_goal_id select (visible only when parent_type='goal')
 *   - Native <input type="date"> for target_date
 *   - Searchable select for metric_id populated from useRorosMetrics
 *
 * Strategy domain is pre-selected when creating (PRD v3.4 GAP 4).
 *
 * Compliance: ART-01 (definition_of_done required), ART-04.
 *
 * DOGFOOD-5: Added daily/weekly horizons; dropdown filtered by tenant planning_cycle:
 *   - planning_cycle='quarterly' → hide 'trimester'
 *   - planning_cycle='trimester' → hide 'quarterly'
 *
 * Wave A F4: anchor (parent_type/parent_goal_id), target_date, owner_actor_id, metric_id.
 * Wave A F5: AI Metric Helper — "Suggest with AI" button on metric_id field.
 *
 * Sheet Migration Wave 3a: outer form converted from Modal to FormSheet.
 * Nested AI Draft modal and Metric Suggestion modal STAY as Modal (R2: modals
 * layer above sheets at z=60 vs z=50). FormSheet handles dirty guard, discard
 * banner, sticky header, scrollable body, and opt-in URL routing.
 *
 * Parent API (isOpen, onClose, onSubmit, editingOutcome, domains,
 * strategyDomainId, isLoading) is unchanged. urlKey / urlId are
 * additional optional props for the URL-routing pilot (G3).
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FormSheet } from '@/components/ui/FormSheet';
import type { TabDef } from '@/components/ui/FormSheet';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Sparkles } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import { aiBusy } from '@/lib/aiBusy';
import { useRorosTenantSettings } from '@/hooks/roros/launchpad';
import { useRorosActors } from '@/hooks/roros/actors';
// H1: useRorosGoals no longer needed directly — AnchorPicker handles it
import { useRorosMetrics } from '@/hooks/roros/artifacts';
import { rorosKeys } from '@/hooks/roros/keys';
import { AnchorPicker } from '@/components/roros/AnchorPicker';
import { RorosArtifactService } from '@/services/RorosService';
import { visibleTimeHorizons } from '@/lib/timeHorizons';
import type { RorosOutcomeDTO } from '@/types/roros/artifacts';
import type { MetricSuggestion } from '@/types/roros/artifacts';
import type { RorosDomainDTO } from '@/types/roros/domains';
// CCA-M2 AC-CCA-11 / AC-CCA-28
import { useAlignmentCheck, useAlignmentAcknowledgements } from '@/hooks/roros/alignment';
import { AlignmentWarnings } from '@/components/roros/alignment/AlignmentWarnings';
import { CompanyContextPanel } from '@/components/roros/alignment/CompanyContextPanel';
import type { AlignmentConflict } from '@/services/roros/alignment.service';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'active', label: t('active') },
  { value: 'achieved', label: t('achieved') },
  { value: 'missed', label: t('missed') },
  { value: 'archived', label: t('archived') },
];

const PARENT_TYPE_OPTIONS = [
  { value: '', label: t('— None —') },
  { value: 'vision', label: t('Under Vision') },
  { value: 'goal', label: t('Under a Goal') },
];

// ─── Snapshot type & diff ─────────────────────────────────────────────────────

/**
 * Captures all editable form state for dirty-detection via FormSheet.
 * FormSheet computes isDirty = Object.values(diff(baseline, current)).some(Boolean).
 */
type OutcomeSnapshot = {
  title: string;
  description: string;
  definitionOfDone: string;
  domainId: string;
  timeHorizon: string;
  status: string;
  parentType: string;
  parentGoalId: string;
  targetDate: string;
  ownerActorId: string;
  metricId: string;
};

function diffOutcomeSnapshots(
  base: OutcomeSnapshot,
  cur: OutcomeSnapshot,
): Partial<Record<keyof OutcomeSnapshot, boolean>> {
  return {
    title: base.title !== cur.title,
    description: base.description !== cur.description,
    definitionOfDone: base.definitionOfDone !== cur.definitionOfDone,
    domainId: base.domainId !== cur.domainId,
    timeHorizon: base.timeHorizon !== cur.timeHorizon,
    status: base.status !== cur.status,
    parentType: base.parentType !== cur.parentType,
    parentGoalId: base.parentGoalId !== cur.parentGoalId,
    targetDate: base.targetDate !== cur.targetDate,
    ownerActorId: base.ownerActorId !== cur.ownerActorId,
    metricId: base.metricId !== cur.metricId,
  };
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface OutcomeFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, unknown>) => void;
  editingOutcome: RorosOutcomeDTO | null;
  domains: RorosDomainDTO[];
  /** The ID (UUID) of the Strategy domain — pre-selected on create. */
  strategyDomainId: string;
  isLoading?: boolean;
  /** URL routing pilot (G3): when set, FormSheet syncs ?urlKey=urlId in the URL. */
  urlKey?: string;
  /** The id value written to the URL param. Required when urlKey is set. */
  urlId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OutcomeFormDialog({
  isOpen,
  onClose,
  onSubmit,
  editingOutcome,
  domains,
  strategyDomainId,
  isLoading = false,
  urlKey,
  urlId,
}: OutcomeFormDialogProps) {
  const dialogTitle = editingOutcome ? t('Edit Outcome') : t('New Outcome');
  const isEditing = !!editingOutcome;

  // CCA-M2 AC-CCA-11: alignment check (non-blocking — outcome ALWAYS saves)
  const alignmentCheckMutation = useAlignmentCheck();
  const [alignmentConflicts, setAlignmentConflicts] = useState<AlignmentConflict[]>([]);
  // AC-CCA-28: prior acks read-back (edit mode only; skipped when no editingOutcome.id)
  const { seededIds: seededAcknowledgedIds } = useAlignmentAcknowledgements(
    editingOutcome?.id ?? null,
    'outcome',
  );
  const [acknowledgedPrincipleIds, setAcknowledgedPrincipleIds] = useState<Set<string>>(new Set());
  // Merge seeded prior acks when they load.
  // Bail (return prev unchanged) when no new ids — prevents infinite loop.
  useEffect(() => {
    if (seededAcknowledgedIds.size > 0) {
      setAcknowledgedPrincipleIds((prev) => {
        const merged = new Set(prev);
        let changed = false;
        seededAcknowledgedIds.forEach((id) => {
          if (!merged.has(id)) { merged.add(id); changed = true; }
        });
        return changed ? merged : prev;
      });
    }
  }, [seededAcknowledgedIds]);

  // ─── Form state via useFormSnapshot (F-Refinement-10) ─────────────────────
  const { state, setField, baseline, current } = useFormSnapshot<OutcomeSnapshot>({
    initialFactory: () => editingOutcome
      ? {
          title: editingOutcome.title,
          description: editingOutcome.description ?? '',
          definitionOfDone: editingOutcome.definitionOfDone,
          domainId: editingOutcome.domainId,
          timeHorizon: editingOutcome.timeHorizon,
          status: editingOutcome.status,
          parentType: editingOutcome.parentType ?? '',
          parentGoalId: editingOutcome.parentGoalId ?? '',
          targetDate: editingOutcome.targetDate ? editingOutcome.targetDate.slice(0, 10) : '',
          ownerActorId: editingOutcome.ownerActorId ?? '',
          metricId: editingOutcome.metricId ?? '',
        }
      : {
          title: '',
          description: '',
          definitionOfDone: '',
          domainId: strategyDomainId,
          timeHorizon: 'quarterly',
          status: 'active',
          parentType: '',
          parentGoalId: '',
          targetDate: '',
          ownerActorId: '',
          metricId: '',
        },
    isOpen,
    depKey: editingOutcome?.id ?? null,
  });
  const { title, description, definitionOfDone, domainId, timeHorizon, status, parentType, parentGoalId, targetDate, ownerActorId, metricId } = state;

  // ─── Tenant settings (planning cycle, AI operator) ─────────────────────────
  const tenantSettingsQuery = useRorosTenantSettings();
  const planningCycle = (tenantSettingsQuery.data as { planning_cycle?: string } | undefined)?.planning_cycle;
  const tenantSettings = tenantSettingsQuery.data as { ai_provider?: string | null } | undefined;
  const hasProvider = !!tenantSettings?.ai_provider;

  const aiActorsQuery = useRorosActors({ type: 'ai_operator' });
  const hasEligibleOperator = useMemo(() => {
    const list = aiActorsQuery.data ?? [];
    return list.some(
      (a) => a.actorType === 'ai_operator'
        && a.status === 'active'
        && (a.adoptionLevel === 'shadow' || a.adoptionLevel === 'copilot' || a.adoptionLevel === 'autopilot'),
    );
  }, [aiActorsQuery.data]);

  const showAiDraftButton = !isEditing && hasProvider && hasEligibleOperator;
  // H2 fix: gate the metric helper on the same conditions as the AI draft button.
  // Previously it showed unconditionally — users without a configured operator
  // would hit a runtime error on click. Now hidden when no operator available.
  const showAiMetricHelper = hasProvider && hasEligibleOperator;

  // ─── AI Draft state ─────────────────────────────────────────────────────────
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [aiPromptText, setAiPromptText] = useState('');
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiDrafted, setAiDrafted] = useState(false);

  // ─── F5 — AI Metric Suggestion state ─────────────────────────────────────
  const [showMetricSuggest, setShowMetricSuggest] = useState(false);
  const [metricSuggesting, setMetricSuggesting] = useState(false);
  const [metricSuggestions, setMetricSuggestions] = useState<MetricSuggestion[]>([]);
  const [creatingMetricIdx, setCreatingMetricIdx] = useState<number | null>(null);

  // ─── External data for selects ────────────────────────────────────────────
  const allActorsQuery = useRorosActors();
  const queryClient = useQueryClient();

  // H1: Goals for parent_goal_id select now delegated to AnchorPicker
  const effectiveDomainId = domainId || strategyDomainId;

  // Metrics for metric_id select — scoped to current domain
  const metricsQuery = useRorosMetrics(effectiveDomainId);
  const metrics = metricsQuery.data ?? [];

  // Reset AI draft ephemeral state on open (form fields handled by useFormSnapshot).
  useEffect(() => {
    if (!isOpen) return;
    setAiDrafted(false);
    setShowAiPrompt(false);
    setAiPromptText('');
    setShowMetricSuggest(false);
    setMetricSuggestions([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingOutcome?.id]);

  // Reset metric suggestion when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setShowMetricSuggest(false);
      setMetricSuggestions([]);
    }
  }, [isOpen]);

  // ─── Validation & save ────────────────────────────────────────────────────
  const handleSave = () => {
    if (!title.trim() || !definitionOfDone.trim()) {
      showToast.error(t('Title and Definition of Done are required.'));
      return;
    }

    const normalized: Record<string, unknown> = {
      title: title.trim(),
      description: description.trim() || null,
      definition_of_done: definitionOfDone.trim(),
      domainId: domainId || strategyDomainId,  // A1: camelCase on API surface
      time_horizon: timeHorizon,
      status,
      parent_type: parentType || null,
      parent_goal_id: parentType === 'goal' ? (parentGoalId || null) : null,
      target_date: targetDate || null,
      owner_actor_id: ownerActorId || null,
      metric_id: metricId || null,
    };

    // CCA-M2 AC-CCA-11: outcome saves FIRST (non-blocking), then check alignment.
    // The outcome ALWAYS saves regardless of alignment result.
    onSubmit(normalized);

    // Run alignment check after save (non-blocking — any conflict is a warning only).
    // LLM error → outcome still saved above; conflicts stay empty.
    alignmentCheckMutation.mutate(
      { subject: { title: title.trim(), description: description.trim() || undefined } },
      {
        onSuccess: (result) => {
          if (result.status === 'ok' && result.conflicts.length > 0) {
            setAlignmentConflicts(result.conflicts);
          }
        },
        onError: () => {
          // LLM error → outcome already saved; silently swallow (no toast)
        },
      },
    );

    if (!metricId) {
      showToast.warning(t('This outcome has no metric — how will you measure success?'));
    }
  };

  // ─── AI Draft handler ─────────────────────────────────────────────────────
  const handleAiDraftSubmit = useCallback(async () => {
    const promptText = aiPromptText.trim();
    if (!promptText) {
      showToast.error(t('Describe the outcome you want a draft for.'));
      return;
    }
    setAiDrafting(true);
    aiBusy.incr();
    try {
      const draft = await RorosArtifactService.draftOutcome({
        prompt: promptText,
        domainId: domainId || strategyDomainId || undefined,  // A1
      });
      setField('title', draft.title ?? '');
      setField('description', draft.description ?? '');
      setField('definitionOfDone', draft.definition_of_done ?? '');
      setField('domainId', strategyDomainId);
      setField('timeHorizon', draft.time_horizon ?? 'quarterly');
      setAiDrafted(true);
      setShowAiPrompt(false);
      setAiPromptText('');
      const sotNote = draft.sotContextTitles && draft.sotContextTitles.length > 0
        ? t('Draft is a suggestion only — review before saving.')
        : t('Draft is a suggestion only — no Sources of Truth recorded yet.');
      showToast.success(`${t('AI draft ready')} — ${sotNote}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('Failed to draft outcome');
      if (/NO_AI_OPERATOR_AVAILABLE/i.test(message)) {
        showToast.error(t('Configure an AI Operator first.'));
      } else if (/ROROS_BUDGET_EXHAUSTED/i.test(message)) {
        showToast.error(t('AI budget exhausted — increase the cap or wait for the next window.'));
      } else if (/ROROS_OPERATOR_OUT_OF_SCOPE/i.test(message)) {
        showToast.error(t("The AI Operator's Role Contract does not cover this domain."));
      } else {
        showToast.error(message);
      }
    } finally {
      setAiDrafting(false);
      aiBusy.decr();
    }
  }, [aiPromptText, domainId, strategyDomainId]);

  // ─── F5 — Metric suggestion handler ──────────────────────────────────────
  const handleMetricSuggest = useCallback(async () => {
    const outcomeTitle = title.trim();
    const dod = definitionOfDone.trim();
    const scope = domainId || strategyDomainId || '';
    if (!outcomeTitle || !dod) {
      showToast.error(t('Fill in Title and Definition of Done first.'));
      return;
    }
    if (!scope) {
      showToast.error(t('Select a Domain first.'));
      return;
    }
    setMetricSuggesting(true);
    aiBusy.incr();
    try {
      const result = await RorosArtifactService.suggestMetric({
        title: outcomeTitle,
        description: description.trim() || undefined,
        definition_of_done: dod,
        domainId: scope,  // A1
      });
      setMetricSuggestions(result.suggestions);
      setShowMetricSuggest(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('Failed to suggest metrics');
      if (/NO_AI_OPERATOR_AVAILABLE/i.test(message)) {
        showToast.error(t('Configure an AI Operator first.'));
      } else {
        showToast.error(message);
      }
    } finally {
      setMetricSuggesting(false);
      aiBusy.decr();
    }
  }, [title, definitionOfDone, description, domainId, strategyDomainId]);

  // "Use this" — create the metric then set metric_id.
  //
  // Sven dogfood 2026-05-02: when the AI helper creates a metric, the new metric
  // must (a) appear in the dropdown immediately, AND (b) be auto-selected.
  // Bug pre-fix: `RorosArtifactService.createMetric` is called directly (not via
  // `useRorosMetricCreate`), so React Query's cache for the metrics list isn't
  // invalidated. The dropdown stays stale and the new id has no matching option
  // → setMetricId(metric.id) wires up the form value but the user sees an empty
  // dropdown until the page reloads. Fix: invalidate both metrics caches
  // (byDomain + allForOrg) so the dropdown re-fetches and the new option lands
  // before setMetricId runs. Await the invalidation so the option exists when
  // the value is set.
  const handleUseMetricSuggestion = useCallback(async (
    suggestion: MetricSuggestion,
    idx: number,
  ) => {
    const scope = domainId || strategyDomainId || '';
    setCreatingMetricIdx(idx);
    try {
      const metric = await RorosArtifactService.createMetric(scope, {
        title: suggestion.title,
        description: suggestion.description || null,
        target_value: suggestion.target_value,
        threshold_warning: suggestion.threshold_warning,
        threshold_critical: suggestion.threshold_critical,
        direction: suggestion.direction as 'higher_better' | 'lower_better' | 'target',
        cadence: suggestion.cadence as 'daily' | 'weekly' | 'monthly' | 'quarterly',
        indicator_type: suggestion.indicator_type as 'leading' | 'lagging',
        source_of_truth: suggestion.source_of_truth,
        status: 'active',
      });

      // Refresh the metrics dropdown so the new option is visible BEFORE we
      // select it. byDomain is the cache the dropdown reads; allForOrg is
      // refreshed for the Role Contract editor and other consumers.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: rorosKeys.metrics.byDomain(scope) }),
        queryClient.invalidateQueries({ queryKey: rorosKeys.metrics.allForOrg }),
      ]);

      setField('metricId', metric.id);
      setShowMetricSuggest(false);
      showToast.success(t('Metric created and linked to outcome.'));
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : t('Failed to create metric'));
    } finally {
      setCreatingMetricIdx(null);
    }
  }, [domainId, strategyDomainId, queryClient]);

  // ─── Select options ───────────────────────────────────────────────────────
  const visibleHorizons = useMemo(
    () => visibleTimeHorizons(planningCycle).map((o) => ({ value: o.value, label: t(o.value) })),
    [planningCycle],
  );

  const domainOptions = useMemo(
    () => [
      { value: '__none__', label: t('Select a domain…') },
      ...domains.map((d) => ({ value: d.id, label: d.name })),
    ],
    [domains],
  );

  const actorOptions = useMemo(() => {
    const list = allActorsQuery.data ?? [];
    return [
      { value: '__none__', label: t('— None —') },
      ...list.filter((a) => a.status === 'active').map((a) => ({ value: a.id, label: a.name })),
    ];
  }, [allActorsQuery.data]);

  // H1: goalOptions removed — AnchorPicker builds them internally

  const metricOptions = useMemo(() => [
    { value: '__none__', label: t('— None —') },
    ...metrics.map((m) => ({ value: m.id, label: m.title })),
  ], [metrics]);

    // ─── Tab content: Details (all form fields + alignment flag) ─────────────────
  // Defined before return so it can be passed as tab.content.
  // CompanyContextPanel removed from here (moved to dedicated Company Context tab).
  const detailsTabContent = (
    <div className="space-y-4">
      {/* AI Draft banner */}
          {showAiDraftButton && (
            <div className="flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
              <div className="text-xs text-primary-900">
                {aiDrafted
                  ? t('Draft prefilled by AI — review and edit before saving.')
                  : t('Save time: let an AI Operator draft this outcome for you.')}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAiPrompt(true)}
                leftIcon={<Sparkles className="h-3.5 w-3.5" />}
                data-testid="outcome-ai-draft-button"
              >
                {t('Draft with AI')}
              </Button>
            </div>
          )}

          {/* 1. Title */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1" htmlFor="outcome-title">
              {t('Title')} <span className="text-destructive">*</span>
            </label>
            <Input
              id="outcome-title"
              value={title}
              onChange={(e) => setField('title', e.target.value)}
              placeholder={t('e.g. Increase customer retention to 95%')}
              data-testid="outcome-title-input"
            />
          </div>

          {/* 2+3. Anchor — H1: delegated to shared AnchorPicker component */}
          <div>
            <span className="text-sm font-medium text-foreground block mb-1">{t('Anchor')}</span>
            <AnchorPicker
              mode="outcome"
              parentType={parentType === '' ? null : (parentType as 'vision' | 'goal' | 'outcome')}
              parentId={parentGoalId || null}
              domainId={effectiveDomainId}
              onChange={(pt, pid) => {
                setField('parentType', pt ?? '');
                setField('parentGoalId', pid ?? '');
              }}
            />
          </div>

          {/* 4. Target date */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1" htmlFor="outcome-target-date">
              {t('Target Date')}
            </label>
            <Input
              id="outcome-target-date"
              type="date"
              value={targetDate}
              onChange={(e) => setField('targetDate', e.target.value)}
              data-testid="outcome-target-date-input"
            />
          </div>

          {/* 5. Accountable */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1" htmlFor="outcome-owner">
              {t('Accountable')}
            </label>
            <Select
              id="outcome-owner"
              value={ownerActorId || '__none__'}
              onChange={(e) => setField('ownerActorId', e.target.value === '__none__' ? '' : e.target.value)}
              options={actorOptions}
              data-testid="outcome-owner-select"
            />
          </div>

          {/* 6. Definition of Done */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1" htmlFor="outcome-dod">
              {t('Definition of Done')} <span className="text-destructive">*</span>
            </label>
            <Textarea
              id="outcome-dod"
              value={definitionOfDone}
              onChange={(e) => setField('definitionOfDone', e.target.value)}
              placeholder={t('Evidence that shows this outcome is achieved')}
              rows={3}
              hint={t('What evidence will prove this is shipped? See Verification Policy in Knowledge items for evidence standards.')}
              data-testid="outcome-dod-input"
            />
          </div>

          {/* 7. Metric (with AI suggest banner) */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1" htmlFor="outcome-metric">
              {t('Metric')}
            </label>
            <Select
              id="outcome-metric"
              value={metricId || '__none__'}
              onChange={(e) => setField('metricId', e.target.value === '__none__' ? '' : e.target.value)}
              options={metricOptions}
              data-testid="outcome-metric-select"
            />
            <div className="mt-1 flex items-center gap-3">
              <p className="text-xs text-muted-foreground flex-1">
                {metricId
                  ? t('Metric linked. You can change it by selecting a different metric or suggesting a new one.')
                  : t('No metric set — a metric tells you how to measure success.')}
              </p>
              <Link
                to="/roros/artifacts/metrics"
                className="text-xs text-primary hover:underline whitespace-nowrap"
                onClick={onClose}
              >
                {t('Open Metrics →')}
              </Link>
            </div>
            {/* AI Suggest button — H2: gated on showAiMetricHelper */}
            {showAiMetricHelper && (
              <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-xs text-amber-900">
                  {t('No metric found? Suggest one with AI based on this outcome.')}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  isLoading={metricSuggesting}
                  onClick={handleMetricSuggest}
                  leftIcon={<Sparkles className="h-3.5 w-3.5" />}
                  data-testid="outcome-metric-suggest-button"
                >
                  {t('Suggest with AI')}
                </Button>
              </div>
            )}
          </div>

          {/* 8. Time Horizon */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1" htmlFor="outcome-horizon">
              {t('Time Horizon')}
            </label>
            <Select
              id="outcome-horizon"
              value={timeHorizon}
              onChange={(e) => setField('timeHorizon', e.target.value)}
              options={visibleHorizons}
              data-testid="outcome-horizon-select"
            />
          </div>

          {/* 9. Domain */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1" htmlFor="outcome-domain">
              {t('Domain')}
            </label>
            <Select
              id="outcome-domain"
              value={domainId || '__none__'}
              onChange={(e) => setField('domainId', e.target.value === '__none__' ? '' : e.target.value)}
              options={domainOptions}
              data-testid="outcome-domain-select"
            />
          </div>

          {/* 10. Description */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1" htmlFor="outcome-description">
              {t('Description')}
            </label>
            <Textarea
              id="outcome-description"
              value={description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder={t('Optional context or background')}
              rows={2}
              data-testid="outcome-description-input"
            />
          </div>

          {/* 11. Status (edit only) */}
          {isEditing && (
            <div>
              <label className="text-sm font-medium text-foreground block mb-1" htmlFor="outcome-status">
                {t('Status')}
              </label>
              <Select
                id="outcome-status"
                value={status}
                onChange={(e) => setField('status', e.target.value)}
                options={STATUS_OPTIONS}
                data-testid="outcome-status-select"
              />
            </div>
          )}

          {/* CCA-M2 AC-CCA-11: alignment warnings (non-blocking; appear after save) */}
          {alignmentConflicts.length > 0 && (
            <div className="pt-2 border-t border-warning-200">
              <AlignmentWarnings
                conflicts={alignmentConflicts}
                acknowledgedIds={acknowledgedPrincipleIds}
                acknowledgeEnabled={false}
                data-testid="outcome-alignment-warnings"
              />
            </div>
          )}

          {/* CCA-M2 AC-CCA-11: on-demand alignment check button */}
          {alignmentConflicts.length === 0 && (
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!title.trim()) return;
                  alignmentCheckMutation.mutate(
                    { subject: { title: title.trim(), description: description.trim() || undefined } },
                    {
                      onSuccess: (result) => {
                        if (result.status === 'ok' && result.conflicts.length > 0) {
                          setAlignmentConflicts(result.conflicts);
                        }
                      },
                      // Fix [5]: provide onError so LLM/network failures show a soft hint
                      // rather than leaving the spinner stuck with no feedback.
                      onError: () => {
                        // Soft failure — outcome is not saved here, just a check.
                        // Show no chip; the button returns to idle so user can retry.
                        // No toast needed — isPending flips back to false naturally.
                      },
                    },
                  );
                }}
                disabled={alignmentCheckMutation.isPending || !title.trim()}
                data-testid="outcome-check-alignment"
              >
                {alignmentCheckMutation.isPending ? t('Checking…') : t('Check alignment')}
              </Button>
            </div>
          )}

    </div>
  );

  // ─── Tab content: Company Context ─────────────────────────────────────────────
  const companyContextTabContent = (
    <div className="pt-2">
      <CompanyContextPanel data-testid="outcome-company-context-panel" />
    </div>
  );

  // ─── Tabs definition ──────────────────────────────────────────────────────────
  const outcomeTabs: Array<TabDef<OutcomeSnapshot>> = [
    {
      key: 'details',
      label: t('Details'),
      fields: ['title', 'description', 'definitionOfDone', 'domainId', 'timeHorizon', 'status', 'parentType', 'parentGoalId', 'targetDate', 'ownerActorId', 'metricId'] as Array<keyof OutcomeSnapshot>,
      content: detailsTabContent,
      testId: 'outcome-tab-details',
    },
    {
      key: 'context',
      label: t('Company Context'),
      content: companyContextTabContent,
      testId: 'outcome-tab-context',
    },
  ];

  return (
    <>
      {/*
        Outer form: FormSheet (Wave 3a).
        Nested AI Draft + Metric Suggestion Modals sit OUTSIDE this FormSheet
        and render via the fragment below. Per design contract R2, Modals (z=60)
        layer above Sheets (z=50) — so the AI modals correctly stack on top.
      */}
      <FormSheet<OutcomeSnapshot>
        isOpen={isOpen}
        onClose={onClose}
        title={dialogTitle}
        mode={isEditing ? 'edit' : 'create'}
        baseline={baseline}
        current={current}
        diff={diffOutcomeSnapshots}
        onSubmit={handleSave}
        isSubmitting={isLoading || aiDrafting}
        submitLabel={isEditing ? t('Save') : t('Create Outcome')}
        submittingLabel={t('Saving…')}
        submitTestId="outcome-save-button"
        width="md"
        urlKey={urlKey}
        urlId={urlId}
        tabs={outcomeTabs}
        tabsAriaLabel={t('Outcome editor sections')}
      />

      {/* J7-W1 — AI Draft prompt modal (stays Modal per R2: layers above Sheet at z=60) */}
      <Modal
        isOpen={showAiPrompt}
        onClose={() => {
          if (aiDrafting) return;
          setShowAiPrompt(false);
        }}
        title={t('Draft Outcome with AI')}
        size="md"
        footer={
          <div className="flex items-center justify-between w-full">
            <Link
              to="/roros/actors"
              className="text-xs text-muted-foreground hover:underline"
              onClick={() => setShowAiPrompt(false)}
            >
              {t('Manage Actors →')}
            </Link>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAiPrompt(false)}
                disabled={aiDrafting}
              >
                {t('Cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleAiDraftSubmit}
                disabled={aiDrafting || !aiPromptText.trim()}
                data-testid="outcome-ai-draft-submit"
              >
                {aiDrafting ? t('Drafting...') : t('Draft')}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t('Describe the outcome you want to achieve. The AI Operator runs in Shadow / Copilot mode — your draft is a suggestion, never auto-saved.')}
          </p>
          <Textarea
            label={t('What outcome are you aiming for?')}
            value={aiPromptText}
            onChange={(e) => setAiPromptText(e.target.value)}
            placeholder={t('e.g. Sign 3 pilot customers by end of month')}
            rows={4}
            disabled={aiDrafting}
            data-testid="outcome-ai-draft-prompt"
          />
          <p className="text-xs text-muted-foreground italic">
            {t('Draft is a suggestion only — review before saving.')}
          </p>
        </div>
      </Modal>

      {/* F5 — AI Metric Suggestions modal (stays Modal per R2: layers above Sheet at z=60) */}
      <Modal
        isOpen={showMetricSuggest}
        onClose={() => setShowMetricSuggest(false)}
        title={t('AI Metric Suggestions')}
        size="md"
        footer={
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowMetricSuggest(false)}
            >
              {t('Close')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t('Select a suggestion to create the metric and link it to this outcome.')}
          </p>
          {metricSuggestions.length === 0 && (
            <p className="text-sm text-muted-foreground italic">{t('No suggestions available.')}</p>
          )}
          {metricSuggestions.map((s, idx) => (
            <div key={idx} className="rounded-md border border-border p-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{s.title}</p>
                  {s.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {s.direction}
                    </span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {s.cadence}
                    </span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {s.indicator_type}
                    </span>
                    {s.source_of_truth && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {t('Source')}: {s.source_of_truth}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  isLoading={creatingMetricIdx === idx}
                  disabled={creatingMetricIdx !== null}
                  onClick={() => handleUseMetricSuggestion(s, idx)}
                  data-testid={`metric-suggestion-use-${idx}`}
                >
                  {t('Use this')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
}
